import { useMemo } from 'react';
import { gql, useQuery } from "@apollo/client";
import { useTranslation } from 'react-i18next';

export const BUSINESS_SCENARIO_ORDER = 1001001;
export const BUSINESS_SCENARIO_DSP = 1001002;
export const BUSINESS_SCENARIO_COST_PERIODIC = 1001003;
/** 收入侧分拣段计费场景：不作为顶部业务场景下拉的独立选项，仅用于"客户计费"下按费用项场景二次筛选 */
export const BUSINESS_SCENARIO_SORTING = 1001004;

/**
 * "客户计费"（BUSINESS_SCENARIO_ORDER）下，定价方案配置/查看/编辑页里用于筛选费用项 Tab 展示范围的
 * 二级选择框选项：费用项本身的 businessScenarioCode 为 1001001（收入侧尾程）或 1001004（收入侧分拣）。
 */
export const FEE_ITEM_SCENARIO_FILTER_OPTIONS: { value: number; label: string }[] = [
  { value: BUSINESS_SCENARIO_ORDER, label: '收入侧尾程费用项' },
  { value: BUSINESS_SCENARIO_SORTING, label: '收入侧分拣费用项' },
];

export const COST_PERIODIC_FEE_ITEM_CODE = 1001003001;

/** 周期性计费方案绑定"全部HUB"时使用的兜底 bindValue */
export const COST_PERIODIC_BIND_ALL_VALUE = 'ALL';

export interface PricingScenario {
  id: string;
  businessScenarioCode: number;
  businessScenarioName: string;
  businessCode: number;
  description?: string | null;
}

/** 查询所有业务场景并应用 i18n 转换，调用方无需自行查询数据 */
export function usePricingScenarios(): { scenarios: PricingScenario[]; loading: boolean } {
  const { t } = useTranslation();
  const { data, loading } = useQuery(GET_ALL_PRICING_SCENARIOS);
  const scenarios = useMemo<PricingScenario[]>(
    () => (data?.allPricingScenarios ?? []).map((s: PricingScenario) => {
      const code = Number(s.businessScenarioCode);
      let businessScenarioName = s.businessScenarioName;
      if (code === BUSINESS_SCENARIO_ORDER) {
        businessScenarioName = t('settlement.pricingQuery.businessScenario.clientPricing');
      } else if (code === BUSINESS_SCENARIO_DSP) {
        businessScenarioName = t('settlement.pricingQuery.businessScenario.dspPricing');
      } else if (code === BUSINESS_SCENARIO_COST_PERIODIC) {
        businessScenarioName = t('settlement.pricingQuery.businessScenario.costPeriodicPricing');
      }
      return { ...s, businessScenarioName };
    }),
    [data, t]
  );
  return { scenarios, loading };
}

export function toPricingScenarioSelectOptions(
  scenarios: Pick<PricingScenario, 'businessScenarioCode' | 'businessScenarioName'>[]
): { value: number; label: string }[] {
  // businessScenarioCode 来自 GraphQL Long 标量，Apollo 经常将其序列化为字符串（如 "1001001"）而不是
  // number；这里显式转换成 number，避免下游用 === 严格比较该值（例如 CreateSchemeComp.tsx 的
  // "费用项范围"选择框）时因类型不一致而恒为 false。
  return scenarios.map(s => ({ value: Number(s.businessScenarioCode), label: s.businessScenarioName }));
}

/**
 * 按收入侧/支出侧权限过滤业务场景，ORDER 场景受 hasRevenueAccess 控制，DSP 场景受 hasCostAccess 控制
 * 返回已过滤且已做 i18n 转换的 scenarioOptions，调用方无需自行过滤或转换
 */
export function useFilteredPricingScenarios(
  hasRevenueAccess: boolean,
  hasCostAccess: boolean,
  hasCostPeriodicAccess: boolean = false
): { scenarios: PricingScenario[]; loading: boolean; scenarioOptions: { value: number; label: string }[] } {
  const { scenarios: allScenarios, loading } = usePricingScenarios();
  const scenarios = useMemo(
    () => allScenarios.filter(s => {
      const code = Number(s.businessScenarioCode);
      if (code === BUSINESS_SCENARIO_ORDER) return hasRevenueAccess;
      if (code === BUSINESS_SCENARIO_DSP) return hasCostAccess;
      if (code === BUSINESS_SCENARIO_COST_PERIODIC) return hasCostPeriodicAccess;
      return false;
    }),
    [allScenarios, hasRevenueAccess, hasCostAccess, hasCostPeriodicAccess]
  );
  const scenarioOptions = toPricingScenarioSelectOptions(scenarios);
  return { scenarios, loading, scenarioOptions };
}

// 获取所有结算客户选项
export const GET_ALL_SETTLEMENT_CLIENT = gql`
  query SettlementClientQuery {
    allOptions: getAllSettlementClient {
      ...SelectOptionFields
      __typename
    }
  }
  
  fragment SelectOptionFields on SelectOptionType {
    value
    label
    __typename
  }
`;

// 获取所有结算费用项选项
export const GET_ALL_SETTLEMENT_FEE_ITEM = gql`
  query SettlementFeeItemQuery {
    allOptions: getAllSettlementFeeItem {
      ...SelectOptionFields
      __typename
    }
  }
  
  fragment SelectOptionFields on SelectOptionType {
    value
    label
    __typename
  }
`;

// 获取所有业务场景列表
export const GET_ALL_PRICING_SCENARIOS = gql`
  query PricingScenarioQuery {
    allPricingScenarios: getAllPricingScenarios {
      id
      businessScenarioCode
      businessScenarioName
      businessCode
      description
      __typename
    }
  }
`;

// 根据业务场景代码获取定价费用项列表
export const GET_PRICING_FEE_ITEMS_BY_SCENARIO = gql`
  query GetPricingFeeItemsByBusinessScenarioCode($businessScenarioCode: Long!) {
    getPricingFeeItemsByBusinessScenarioCode(businessScenarioCode: $businessScenarioCode) {
      id
      businessFeeCode
      businessFeeName
      businessScenarioCode
      businessCode
      description
      __typename
    }
  }
`;

// 根据费用项编码获取计费因子列表
export const GET_PRICING_FEE_ITEM_FACTORS = gql`
  query GetPricingFeeItemFactorsByFeeItemCode($feeItemCode: Long!) {
    getPricingFeeItemFactorsByFeeItemCode(feeItemCode: $feeItemCode) {
      id
      feeItemCode
      pricingFactor
      __typename
    }
  }
`;

// 获取所有定价费用项列表
export const GET_ALL_PRICING_FEE_ITEMS = gql`
  query GetAllPricingFeeItems {
    getAllPricingFeeItems {
      id
      businessFeeCode
      businessFeeName
      businessScenarioCode
      businessCode
      description
      __typename
    }
  }
`;

// 获取所有计费因子枚举列表
export const GET_ALL_PRICING_FACTORS = gql`
  query GetAllPricingFactors {
    getAllPricingFactors {
      code
      key
      description
      __typename
    }
  }
`;

// 获取所有计费节点枚举列表
export const GET_ALL_PRICING_NODE_ENUMS = gql`
  query GetAllPricingNodeEnums {
    getAllPricingNodeEnums {
      code
      description
      __typename
    }
  }
`;

// 保存费用项与计费因子关系
export const SAVE_FEE_ITEM_FACTORS = gql`
  mutation SaveFeeItemFactors($input: SaveFeeItemFactorInput!) {
    saveFeeItemFactors(input: $input) {
      success
      message
      savedCount
      __typename
    }
  }
`;

// 保存计费因子
export const SAVE_PRICING_FACTOR = gql`
  mutation SavePricingFactor($input: SavePricingFactorInput!) {
    savePricingFactor(input: $input) {
      success
      message
      id
      __typename
    }
  }
`;

// 按父计费因子code分组获取所有计费因子
export const GET_PRICING_FACTORS_GROUPED_BY_PARENT_CODE = gql`
  query GetPricingFactorsGroupedByParentCode {
    getPricingFactorsGroupedByParentCode {
      parentFactorCode
      factors {
        factorName
        factorValue
        __typename
      }
      __typename
    }
  }
`;

// 获取导出任务状态
export const GET_EXPORT_TASK_STATUS = gql`
  query GetExportTaskStatus($taskId: ID!) {
    getExportTaskStatus(taskId: $taskId) {
      taskId
      status
      progress
      message
      downloadUrl
      createdAt
      completedAt
    }
  }
`;

// 获取导出文件列表
export const GET_EXPORTED_FILES = gql`
  query GetExportedFiles($filters: ExportFileFiltersInput, $pageRequest: PageRequestTypeInput) {
    getExportedFiles(filters: $filters, pageRequest: $pageRequest) {
      content {
        fileId
        fileName
        fileSize
        downloadUrl
        exportType
        status
        createdAt
        expiresAt
      }
      pageInfo {
        page
        size
        totalElements
        totalPages
        hasNext
        hasPrevious
      }
    }
  }
`;
